import ElementMap from './elements/element_map';
import TextElement from './elements/text_element';
import ImageElement from './elements/image_element';
import LineElement from './elements/line_element';
import ElementFactory from './elements/element_factory';
import ElementIdGenerator from './elements/element_id_generator';
import ColorUtils from '../color_utils';
import SelectionManager from './selection_manager';
import DragManager from './drag_manager';
import ResizeManager from './resize_manager';
import ElementActionsManager from './element_actions_manager';
import HistoryManager from './history_manager';
import MarqueeSelection from './marquee_selection';
import SnapLineManager from './snap_line_manager';

export default class Canvas {
  // ===========================================================================
  // Static Constants
  // ===========================================================================

  static DEFAULT_BACKGROUND_COLOR = '#ffffff';

  static KEYBOARD_SHORTCUTS = {
    escape: {
      action: 'deselectAll',
      editModeAction: 'exitEditMode'
    },
    'ctrl+z': { action: 'undo', skipWhenFormFieldActive: true },
    'meta+z': { action: 'undo', skipWhenFormFieldActive: true },
    'ctrl+y': { action: 'redo', skipWhenFormFieldActive: true },
    'ctrl+shift+z': { action: 'redo', skipWhenFormFieldActive: true },
    'meta+shift+z': { action: 'redo', skipWhenFormFieldActive: true },
    'ctrl+c': { action: 'copySelected', requiresSelection: true, skipWhenFormFieldActive: true },
    'meta+c': { action: 'copySelected', requiresSelection: true, skipWhenFormFieldActive: true },
    'ctrl+v': { action: 'pasteElements', skipWhenFormFieldActive: true },
    'meta+v': { action: 'pasteElements', skipWhenFormFieldActive: true },
    delete: { action: 'deleteSelected', requiresSelection: true, skipWhenFormFieldActive: true },
    backspace: { action: 'deleteSelected', requiresSelection: true, skipWhenFormFieldActive: true }
  };

  static ARROW_KEYS = {
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0]
  };

  // ===========================================================================
  // Constructor
  // ===========================================================================

  constructor(canvasElement) {
    this.canvasElement = canvasElement;
    this.elementMap = new ElementMap();
    this.isLoadingState = false;
    this.isMultiSelecting = false;

    this._initializeSnapLines();
    this._initializeManagers();
    this._setupEventListeners();

    window._promote_canvas = this; // For debugging
  }

  // ===========================================================================
  // Public API - Element Creation
  // ===========================================================================

  createTextElement(text, x, y, properties = {}) {
    const elementInstance = new TextElement(this.managers, text, x, y, properties);

    this._addElement(elementInstance);
    this.selectElement(elementInstance.element);
    this._saveStateToHistory();

    return elementInstance.element;
  }

  createImageElement(src, x, y, key = null, properties = {}) {
    const elementInstance = new ImageElement(this.managers, src, x, y, key, properties);

    this._addElement(elementInstance);
    this.selectElement(elementInstance.element);
    this._saveStateToHistory();

    return elementInstance.element;
  }

  createLineElement(x, y, width, properties = {}) {
    const elementInstance = new LineElement(this.managers, x, y, width, properties);

    this._addElement(elementInstance);
    this.selectElement(elementInstance.element);
    this._saveStateToHistory();

    return elementInstance.element;
  }

  // ===========================================================================
  // Public API - Background
  // ===========================================================================

  getBackgroundColor() {
    return this.canvasElement.style.backgroundColor || Canvas.DEFAULT_BACKGROUND_COLOR;
  }

  setBackgroundColor(color) {
    this.canvasElement.style.backgroundColor = color;

    if (!this.isLoadingState) {
      this._saveStateToHistory();
    }
  }

  getBackgroundImage() {
    const bgImage = this.canvasElement.style.backgroundImage;
    if (!bgImage || bgImage === 'none' || bgImage === '') return null;

    const match = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);

    return match ? match[1] : null;
  }

  setBackgroundImage(imageUrl, key = null) {
    if (!imageUrl) {
      this.canvasElement.style.backgroundImage = '';
      this.canvasElement.style.backgroundSize = '';
      this.canvasElement.style.backgroundRepeat = '';
      this.canvasElement.removeAttribute('data-key');

      if (!this.isLoadingState) {
        this._saveStateToHistory();
      }
      return;
    }

    this.canvasElement.style.backgroundImage = `url(${imageUrl})`;
    this.canvasElement.style.backgroundSize = '100% 100%';
    this.canvasElement.style.backgroundRepeat = 'no-repeat';

    if (key) this.canvasElement.setAttribute('data-key', key);
    if (!this.isLoadingState) this._saveStateToHistory();
  }

  getBackgroundImageKey() {
    return this.canvasElement.getAttribute('data-key');
  }

  // ===========================================================================
  // Public API - Element Queries
  // ===========================================================================

  getElements() {
    return this.canvasElement.querySelectorAll('.canvas-element');
  }

  clearElements() {
    this.getElements().forEach(element => element.remove());
  }

  getImageElementInstances() {
    const imageElements = [];
    const imageDomElements = this.canvasElement.querySelectorAll('.image-element');

    imageDomElements.forEach((domElement) => {
      const instance = this.elementMap.getInstance(domElement);
      if (instance) imageElements.push(instance);
    });

    return imageElements;
  }

  getWidth() {
    return this.canvasElement.offsetWidth;
  }

  getHeight() {
    return this.canvasElement.offsetHeight;
  }

  // ===========================================================================
  // Public API - Selection
  // ===========================================================================

  selectElement(element) {
    this.selectionManager.selectElement(element);
  }

  deselectAll() {
    this.selectionManager.deselectAll();
  }

  getSelectedInstance() {
    const element = this.selectionManager.getSelectedElements()[0];

    return element ? this.elementMap.getInstance(element) : null;
  }

  hasMultipleSelected() {
    return this.selectionManager.hasMultipleSelected();
  }

  // ===========================================================================
  // Public API - Element Actions
  // ===========================================================================

  deleteSelected() {
    this.elementActionsManager.deleteSelected(this);
    this._saveStateToHistory();
  }

  copySelected() {
    return this.elementActionsManager.copySelected();
  }

  pasteElements() {
    const pastedElements = this.elementActionsManager.pasteElements((data) => {
      return this._createElementFromData(data);
    });

    if (pastedElements.length > 0) {
      this.selectionManager.deselectAll();
      this.selectionManager.selectMultiple(pastedElements);
      this._saveStateToHistory();
    }

    return pastedElements;
  }

  duplicateSelected() {
    this.copySelected();
    this.pasteElements();
  }

  // ===========================================================================
  // Public API - State Serialization
  // ===========================================================================

  loadState(data) {
    const wasLoadingState = this.isLoadingState;
    this.isLoadingState = true;

    if (data.backgroundColor) this.setBackgroundColor(data.backgroundColor);

    if (data.backgroundImage) {
      this.setBackgroundImage(data.backgroundImage.url, data.backgroundImage.key);
    }

    if (data.elements && data.elements.length > 0) {
      data.elements.forEach((elementData) => {
        const instance = ElementFactory.fromData(elementData, this.managers);

        if (instance) {
          this._addElement(instance);
          ElementIdGenerator.setCounter(instance.id);
        }
      });
    }

    this.isLoadingState = wasLoadingState;

    if (!wasLoadingState) this._saveStateToHistory();
  }

  serializeState() {
    const elements = [];
    const canvasElements = this.getElements();

    canvasElements.forEach((domElement) => {
      const instance = this.elementMap.getInstance(domElement);

      if (instance) elements.push(instance.serialize());
    });

    const backgroundColor = ColorUtils.rgbToHex(this.getBackgroundColor());
    const backgroundImage = {
      url: this.getBackgroundImage(),
      key: this.getBackgroundImageKey()
    };

    return {
      elements: elements,
      backgroundColor: backgroundColor,
      backgroundImage: backgroundImage,
      width: this.getWidth(),
      height: this.getHeight()
    };
  }

  // ===========================================================================
  // Public API - History
  // ===========================================================================

  undo() {
    const state = this.historyManager.undo();

    if (state) {
      this._loadStateFromHistory(state);
    }
  }

  redo() {
    const state = this.historyManager.redo();
    if (state) {
      this._loadStateFromHistory(state);
    }
  }

  // ===========================================================================
  // Public API - Cleanup
  // ===========================================================================

  destroy() {
    this._removeEventListeners();
    delete window._promote_canvas;
  }

  // ===========================================================================
  // Private - Initialization
  // ===========================================================================

  _initializeSnapLines() {
    const verticalLine = document.createElement('div');
    verticalLine.classList.add('centerline', '-vertical', 'hidden');
    this.canvasElement.appendChild(verticalLine);

    const horizontalLine = document.createElement('div');
    horizontalLine.classList.add('centerline', '-horizontal', 'hidden');
    this.canvasElement.appendChild(horizontalLine);

    this.centerlineVertical = verticalLine;
    this.centerlineHorizontal = horizontalLine;
  }

  _initializeManagers() {
    this.snapLineManager = new SnapLineManager(this.canvasElement);
    this.selectionManager = new SelectionManager();
    this.historyManager = new HistoryManager();

    this.historyManager.setGetStateCallback(() => {
      return this.isLoadingState ? null : this.serializeState();
    });

    this.resizeManager = new ResizeManager(this.canvasElement);
    this.dragManager = new DragManager(
      this.canvasElement,
      {
        vertical: this.centerlineVertical,
        horizontal: this.centerlineHorizontal
      },
      this.snapLineManager
    );
    this.elementActionsManager = new ElementActionsManager(
      this.selectionManager,
      this.dragManager,
      this.elementMap
    );
    this.marqueeSelection = new MarqueeSelection(this);

    this.managers = {
      selection: this.selectionManager,
      drag: this.dragManager,
      resize: this.resizeManager,
      history: this.historyManager,
      elementActions: this.elementActionsManager
    };
  }

  _setupEventListeners() {
    this._boundMouseDown = this._onMouseDown.bind(this);
    this._boundMouseMove = this._onMouseMove.bind(this);
    this._boundMouseUp = this._onMouseUp.bind(this);
    this._boundKeyDown = this._onKeyDown.bind(this);
    this._boundCanvasClick = this._onCanvasClick.bind(this);

    document.addEventListener('mousemove', this._boundMouseMove);
    document.addEventListener('mouseup', this._boundMouseUp);
    document.addEventListener('keydown', this._boundKeyDown);
    this.canvasElement.addEventListener('click', this._boundCanvasClick);
    this.canvasElement.addEventListener('mousedown', this._boundMouseDown);
  }

  _removeEventListeners() {
    document.removeEventListener('mousemove', this._boundMouseMove);
    document.removeEventListener('mouseup', this._boundMouseUp);
    document.removeEventListener('keydown', this._boundKeyDown);
    this.canvasElement.removeEventListener('click', this._boundCanvasClick);
    this.canvasElement.removeEventListener('mousedown', this._boundMouseDown);
  }

  // ===========================================================================
  // Private - Mouse Event Handlers
  // ===========================================================================

  _onMouseDown(e) {
    if (e.target === this.canvasElement) {
      this.marqueeSelection.startSelection(e);
    }
  }

  _onMouseMove(e) {
    if (this.marqueeSelection.isActive()) {
      this.marqueeSelection.updateSelection(e);
    } else if (this.dragManager.isDraggingActive()) {
      this.dragManager.handleDragMove(e);
    } else if (this.resizeManager.isResizingActive()) {
      this.resizeManager.handleResizeMove(e);
    }
  }

  _onMouseUp() {
    if (this.marqueeSelection.isActive()) {
      const selectedElements = this.marqueeSelection.endSelection();

      if (selectedElements.length > 0) {
        this.selectionManager.selectMultiple(selectedElements);
        this.isMultiSelecting = true;
      }

      return;
    }

    const dragEnded = this.dragManager.endDrag();
    const resizeEnded = this.resizeManager.endResize();

    if (dragEnded || resizeEnded) {
      this._saveStateToHistory();
    }
  }

  _onCanvasClick(e) {
    if (e.target === this.canvasElement && !this.isMultiSelecting) {
      this.deselectAll();
    }

    this.isMultiSelecting = false;
  }

  // ===========================================================================
  // Private - Keyboard Event Handlers
  // ===========================================================================

  _onKeyDown(e) {
    const selectedInstance = this.getSelectedInstance();
    const isEditingText = selectedInstance?.isTextElement() && selectedInstance.isEditing();

    // Handle edit mode exit
    if (isEditingText && e.key === 'Escape') {
      e.preventDefault();

      selectedInstance.exitEditMode();

      return;
    }

    if (isEditingText) return;

    if (this._handleShortcut(e)) return;

    this._handleArrowKeys(e);
  }

  _handleShortcut(event) {
    const shortcutKey = this._getShortcutKey(event);
    if (!shortcutKey) return false;

    const shortcut = Canvas.KEYBOARD_SHORTCUTS[shortcutKey];

    if (!shortcut) return false;

    if (shortcut.skipWhenFormFieldActive && this._isFormFieldActive(event)) return false;
    if (shortcut.requiresSelection && !this.selectionManager.hasSelection()) return false;

    event.preventDefault();

    this[shortcut.action]();

    return true;
  }

  _getShortcutKey(event) {
    if (!event.key) return null;

    const parts = [];

    if (event.ctrlKey) parts.push('ctrl');
    if (event.metaKey) parts.push('meta');
    if (event.shiftKey) parts.push('shift');

    parts.push(event.key.toLowerCase());

    return parts.join('+');
  }

  _handleArrowKeys(event) {
    if (this._isFormFieldActive(event)) return;

    const delta = Canvas.ARROW_KEYS[event.key];
    if (!delta || !this.selectionManager.hasSelection()) return;

    event.preventDefault();

    const step = event.shiftKey ? 10 : 1;

    this._moveSelectedElements(delta[0] * step, delta[1] * step);
  }

  _isFormFieldActive(event) {
    const tagName = event.target.tagName.toLowerCase();
    return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  }

  // ===========================================================================
  // Private - Element Movement
  // ===========================================================================

  _moveSelectedElements(deltaX, deltaY) {
    const elements = this.selectionManager.getSelectedElements();

    elements.forEach(element => {
      const currentLeft = parseInt(element.style.left) || 0;
      const currentTop = parseInt(element.style.top) || 0;

      element.style.left = (currentLeft + deltaX) + 'px';
      element.style.top = (currentTop + deltaY) + 'px';
    });

    this._saveStateToHistory();
  }

  // ===========================================================================
  // Private - Element Management
  // ===========================================================================

  _addElement(elementInstance) {
    this.canvasElement.appendChild(elementInstance.element);
    this.elementMap.store(elementInstance.element, elementInstance);
  }

  _createElementFromData(data) {
    const instance = ElementFactory.fromData(data, this.managers);

    if (instance) {
      this._addElement(instance);
      return instance.element;
    }

    return null;
  }

  // ===========================================================================
  // Private - History Management
  // ===========================================================================

  _saveStateToHistory() {
    if (!this.isLoadingState) {
      const state = this.serializeState();
      this.historyManager.pushState(state);
    }
  }

  _loadStateFromHistory(state) {
    this.isLoadingState = true;

    const elements = this.getElements();
    elements.forEach(el => el.remove());

    this.deselectAll();
    this.loadState(state);

    this.isLoadingState = false;

    document.dispatchEvent(new CustomEvent('promote:canvas--stateLoadedFromHistory'));
  }
}
